import axios from "axios";
import { ApiConnection } from "../config/env";
import ForceLogout, { forceLogout, showPortalErrorAndRedirect } from "../components/Utils/forceLogout";
import { logoutSuccess } from "../redux/authSlice";
import Socket from "../redux/socketConnect";
import Swal from "sweetalert2";

let forceLogoutTriggered = false;
const API_TIMEOUT = 5 * 60 * 1000; // 5 minutes
let lastUserActivityTimestamp = Date.now();
let timeoutRef = null;

const dispatchToggleSessionAlert = () => {
  import("../redux/store").then(({ default: store }) => {
    import("../redux/authSlice").then(({ ToggleSessionAlert }) => {
      store.dispatch(ToggleSessionAlert(true));
    });
  });
};

// Function to reset session timeout based on user activity
const resetSessionTimeout = () => {
  let loggedInUser = JSON.parse(sessionStorage.getItem("userLoggedDetails"));
  if (!loggedInUser) return;

  if (timeoutRef) clearTimeout(timeoutRef);

  timeoutRef = setTimeout(() => {
    const timeSinceLastActivity = Date.now() - lastUserActivityTimestamp;
    if (loggedInUser?.id && timeSinceLastActivity >= API_TIMEOUT) {
      dispatchToggleSessionAlert();
    }
  }, API_TIMEOUT);
};

// Function to track user activity
const trackUserActivity = () => {
  lastUserActivityTimestamp = Date.now();
  resetSessionTimeout();
};

// Attach event listeners for user activity
const startTrackingUserActivity = () => {
  document.addEventListener("mousemove", trackUserActivity);
  document.addEventListener("keydown", trackUserActivity);
  document.addEventListener("click", trackUserActivity);
  resetSessionTimeout();
};

// Remove event listeners when session ends
const stopTrackingUserActivity = () => {
  document.removeEventListener("mousemove", trackUserActivity);
  document.removeEventListener("keydown", trackUserActivity);
  document.removeEventListener("click", trackUserActivity);
  clearTimeout(timeoutRef);
};

// Start tracking
startTrackingUserActivity();

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: ApiConnection.BACKEND_URL,
  timeout: API_TIMEOUT,
});

// Token refresh helpers
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

//  Request interceptor
// // Create a function to retry the original request with the new token
// const retryRequest = async (error) => {
 
//   const originalRequest = error.config;

//   try {
//     // Retrieve the new token from sessionStorage
//     const newToken = sessionStorage.getItem("userLoggedToken");

//     // Clone the original request configuration
//     const retryConfig = {
//       ...originalRequest,
//       headers: {
//         ...originalRequest.headers,
//         Authorization: `Bearer ${newToken}`,
//       },
//     };

//     // Retry the original request using axiosInstanceForJson
//     return await axiosInstance(retryConfig);
//   } catch (retryError) {
//     console.error("Retry request failed:", retryError);
//     return Promise.reject(retryError);
//   }
// };

axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("userLoggedToken");
    if (token) {
      config.headers["Authorization"] = "Bearer " + JSON.parse(token);
    }
    config.headers["Content-Type"] = "application/json";
    return config;
  },
  (error) => {
    console.error("Error in request", error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // 🔄 Reset offline/server flags on success
    import("../redux/store").then(({ default: store }) => {
      import("../redux/authSlice").then(({ setOfflineStatus, setServerDown }) => {
        store.dispatch(setOfflineStatus(false));
        store.dispatch(setServerDown(false));
      });
    });

    return response;
  },

  async (error) => {
    const originalRequest = error.config;
console.log('rerr',error.response);

    if (error.response?.data?.code === 'NEW_DEVICE_LOGIN') {
      const deviceName = error.response?.data?.deviceName;
      const store = await import("../redux/store");

      if (forceLogoutTriggered) {
        return Promise.reject(error); // ⛔ ignore duplicates
      }
    
      forceLogoutTriggered = true; // 🔒 lock immediately
      store.default.dispatch(logoutSuccess(error.response?.status))
       forceLogout(
        error.response?.data?.message,
        deviceName,
      );
      return Promise.reject(error);
    }

    //  NETWORK / SERVER DOWN HANDLING
    if (!error.response) {
      const store = await import("../redux/store");
      const { setOfflineStatus, setServerDown } = await import("../redux/authSlice");

      if (!navigator.onLine) {
        //  No Internet
        store.default.dispatch(setOfflineStatus(true));
      } else {
        //  Server Down / Timeout / Unreachable
        store.default.dispatch(setServerDown(true));
      }

      return Promise.reject(error);
    }

    //  Handle 500 errors as server issues
    if (error.response?.status > 500) {
      const store = await import("../redux/store");
      const { setServerDown } = await import("../redux/authSlice");

      store.default.dispatch(setServerDown(true));
    }

    // Detect if we are currently on a business portal route
    const isBusinessPortal = window.location.pathname.startsWith("/businessSubscriptionPortal");

    //  NEW DEVICE LOGIN — never show this alert inside the business portal
    if (!isBusinessPortal && error.response?.data?.code === "NEW_DEVICE_LOGIN") {
      const deviceName = error.response?.data?.deviceName;
      const store = await import("../redux/store");

      if (forceLogoutTriggered) {
        return Promise.reject(error);
      }

      forceLogoutTriggered = true;
      store.default.dispatch(logoutSuccess(error.response?.status));

      forceLogout(
        error.response?.data?.message,
        deviceName
      );

      return Promise.reject(error);
    }

    // USER_NOT_FOUND — account was deleted while logged in
    if (error.response?.data?.code === "USER_NOT_FOUND") {
      if (forceLogoutTriggered) return Promise.reject(error);
      forceLogoutTriggered = true;
      setTimeout(() => { forceLogoutTriggered = false; }, 500);

      if (isBusinessPortal) {
        showPortalErrorAndRedirect(
          'Account Deleted',
          error.response?.data?.message || 'This account has been deleted and can no longer be accessed.'
        );
      } else {
        const store = await import("../redux/store");
        store.default.dispatch(logoutSuccess(error.response?.status));
        Swal.fire({
          title: 'Account Deleted',
          text: error.response?.data?.message || 'This account has been deleted and can no longer be accessed.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d32f2f',
          allowOutsideClick: false,
        });
      }
      return Promise.reject(error);
    }

    // TOKEN REFRESH FLOW
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Business portal: session expired — show message then redirect to login
      if (isBusinessPortal) {
        if (!forceLogoutTriggered) {
          forceLogoutTriggered = true;
          setTimeout(() => { forceLogoutTriggered = false; }, 500);
          showPortalErrorAndRedirect(
            'Session Expired',
            'Your session has expired. Please log in again to continue.'
          );
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = JSON.parse(sessionStorage.getItem("refreshToken"));
        const accessToken = JSON.parse(
          sessionStorage.getItem("userLoggedToken")
        );

        const store = await import("../redux/store");
        const authSlice = await import("../redux/authSlice");
        Socket.logoutUser(store.default.dispatch);
        await store.default.dispatch(
          authSlice.renewToken([refreshToken], accessToken)
        );

        const newToken = JSON.parse(sessionStorage.getItem("userLoggedToken"));

        processQueue(null, newToken);

        return axiosInstance(originalRequest);
      } catch (renewError) {
        processQueue(renewError, null);
        return Promise.reject(renewError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;